import { Platform } from "react-native";
import { Heading, Label as LabelOG, Paragraph as ParagraphOG, SizableText as SizableTextOG, styled, Text as TextOG, type TextProps } from "tamagui";
import { headingLineHeight, subheadlineLineHeight } from "../../config/fonts";
import { displayLineHeightCompensation } from "../../themes/token-fontSizes";

// =============================================================================
// LEGACY Typography System (Deprecated)
// These components use the old numeric font size tokens ($1-$14)
// =============================================================================

const commonVariants = {
  bold: {
    true: {
      fontWeight: "500"
    }
  },
  italic: {
    true: {
      fontStyle: "italic"
    }
  }
} as const;

/** Extracts numeric size from a Tamagui font token (number or { val: number }) */
function getFontSizeValue(token: unknown): number | undefined {
  if (typeof token === "number") {
    return token;
  }
  if (token != null && typeof token === "object" && "val" in token) {
    const v = token.val;
    return typeof v === "number" ? v : undefined;
  }
  return undefined;
}

/**
 * @deprecated Use V2 Typography components instead
 */
export const Text = styled(TextOG, {
  // Tamagui v2 defaults Text to display: flex on web
  display: Platform.select({
    web: "inline" as const,
    default: "flex" as const
  }),
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use V2 Typography components instead
 */
export const SizableText = styled(SizableTextOG, {
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use Display1-Display5 components instead
 */
export const Headline = styled(Heading, {
  name: "Headline",
  render: "p",
  size: "$9",
  variants: {
    ...commonVariants,
    size: {
      "...size": (val, {
        font
      }) => {
        const sizeVal = getFontSizeValue(font?.size?.[val]) ?? 0;
        return {
          marginTop: -1 * (sizeVal * (1.0 - headingLineHeight)),
          paddingTop: sizeVal * (1.0 - headingLineHeight)
        };
      }
    }
  } as const
});

/**
 * @deprecated Use Display1 instead
 */
export const Headline1 = styled(Headline, {
  name: "Headline1",
  render: "h1",
  fontFamily: "$heading",
  size: "$11",
  fontWeight: "500",
  $gtMd: {
    size: "$13"
  }
});

/**
 * @deprecated Use Title1-Title5 components instead
 */
export const Subheadline = styled(ParagraphOG, {
  name: "Subheadline",
  render: "p",
  variants: {
    ...commonVariants,
    size: {
      "...size": (val, {
        font
      }) => {
        const sizeVal = getFontSizeValue(font?.size?.[val]);
        return {
          fontSize: sizeVal,
          lineHeight: sizeVal != null ? sizeVal * subheadlineLineHeight : undefined
        };
      }
    }
  } as const,
  defaultVariants: {
    size: "$9"
  }
});

/**
 * @deprecated Use Title1 instead
 */
export const Subheadline1 = styled(Subheadline, {
  name: "Subheadline1",
  render: "h2",
  size: "$10"
});

/**
 * @deprecated Use Title2 instead
 */
export const Subheadline2 = styled(Subheadline, {
  name: "Subheadline2",
  render: "h3",
  size: "$9"
});

/**
 * @deprecated Use Title3 instead
 */
export const Subheadline3 = styled(Subheadline, {
  name: "Subheadline3",
  render: "h4",
  size: "$8"
});

/**
 * @deprecated Use Title4 instead
 */
export const Subheadline4 = styled(Subheadline, {
  name: "Subheadline4",
  render: "h5",
  size: "$7"
});

/**
 * @deprecated Use Title5 instead
 */
export const Subheadline5 = styled(Subheadline, {
  name: "Subheadline5",
  size: "$6",
  fontWeight: "400"
});

/**
 * @deprecated Use Body1, Body2, or Body3 instead
 */
export const Paragraph = styled(ParagraphOG, {
  name: "Paragraph",
  render: "p",
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Use Subtitle1 instead
 */
export const Title = styled(Paragraph, {
  name: "Title",
  size: "$5"
});

/**
 * @deprecated Use Body2 instead
 */
export const Paragraph2 = styled(Paragraph, {
  name: "Paragraph2",
  size: "$3"
});

/**
 * @deprecated Use Body3 instead
 */
export const ParagraphSmall = styled(Paragraph, {
  name: "ParagraphSmall",
  size: "$2"
});

/**
 * @deprecated Use Subtitle1, Subtitle2, or Subtitle3 instead
 */
export const Label = styled(LabelOG, {
  fontFamily: "$body",
  variants: {
    ...commonVariants
  } as const
});

/**
 * @deprecated Create a new anchor component using the V2 typography system
 */
export const AnchorText = styled(TextOG, {
  name: "Anchor",
  render: "a",
  role: "link",
  color: "$textSubtle",
  // textDecorationLine: "none",
  fontWeight: "500",
  // Tamagui v2 defaults Text to display: flex on web
  display: Platform.select({
    web: "inline" as const,
    default: "flex" as const
  }),
  hoverStyle: {
    textDecorationLine: "underline"
  },
  variants: {
    ...commonVariants
  } as const
});

// Tighter line heights require paddingTop/marginTop compensation to prevent glyph clipping
export const Display1 = styled(SizableTextOG, {
  name: "Display1",
  render: "h1",
  fontFamily: "$display",
  size: "$display1",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display1,
  marginBottom: Platform.select({
    web: 0,
    default: -displayLineHeightCompensation.display1
  }),
  marginTop: Platform.select({
    web: -displayLineHeightCompensation.display1,
    default: 0
  })
});
export const Display2 = styled(SizableTextOG, {
  name: "Display2",
  render: "h2",
  fontFamily: "$display",
  size: "$display2",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display2,
  marginBottom: Platform.select({
    web: 0,
    default: -displayLineHeightCompensation.display2
  }),
  marginTop: Platform.select({
    web: -displayLineHeightCompensation.display2,
    default: 0
  })
});
export const Display3 = styled(SizableTextOG, {
  name: "Display3",
  render: "h3",
  fontFamily: "$display",
  size: "$display3",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display3,
  marginBottom: Platform.select({
    web: 0,
    default: -displayLineHeightCompensation.display3
  }),
  marginTop: Platform.select({
    web: -displayLineHeightCompensation.display3,
    default: 0
  })
});
export const Display4 = styled(SizableTextOG, {
  name: "Display4",
  render: "h4",
  fontFamily: "$display",
  size: "$display4",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display4,
  marginBottom: Platform.select({
    web: 0,
    default: -displayLineHeightCompensation.display4
  }),
  marginTop: Platform.select({
    web: -displayLineHeightCompensation.display4,
    default: 0
  })
});
export const Display5 = styled(SizableTextOG, {
  name: "Display5",
  render: "h5",
  fontFamily: "$display",
  size: "$display5",
  fontWeight: "500",
  paddingTop: displayLineHeightCompensation.display5,
  marginBottom: Platform.select({
    web: 0,
    default: -displayLineHeightCompensation.display5
  }),
  marginTop: Platform.select({
    web: -displayLineHeightCompensation.display5,
    default: 0
  })
});
export const Title1 = styled(SizableTextOG, {
  name: "Title1",
  render: "h1",
  fontFamily: "$text",
  size: "$title1"
});
export const Title2 = styled(SizableTextOG, {
  name: "Title2",
  render: "h2",
  fontFamily: "$text",
  size: "$title2"
});
export const Title3 = styled(SizableTextOG, {
  name: "Title3",
  render: "h3",
  fontFamily: "$text",
  size: "$title3"
});
export const Title4 = styled(SizableTextOG, {
  name: "Title4",
  render: "h4",
  fontFamily: "$text",
  size: "$title4"
});
export const Title5 = styled(SizableTextOG, {
  name: "Title5",
  render: "h5",
  fontFamily: "$text",
  size: "$title5"
});
export const Subtitle1 = styled(SizableTextOG, {
  name: "Subtitle1",
  render: "p",
  fontFamily: "$text",
  size: "$subtitle1"
});
export const Subtitle2 = styled(SizableTextOG, {
  name: "Subtitle2",
  render: "p",
  fontFamily: "$text",
  size: "$subtitle2"
});
export const Subtitle3 = styled(SizableTextOG, {
  name: "Subtitle3",
  render: "p",
  fontFamily: "$text",
  size: "$subtitle3"
});
export const Body1 = styled(SizableTextOG, {
  name: "Body1",
  render: "p",
  fontFamily: "$text",
  size: "$body1"
});
export const Body2 = styled(SizableTextOG, {
  name: "Body2",
  render: "p",
  fontFamily: "$text",
  size: "$body2"
});
export const Body3 = styled(SizableTextOG, {
  name: "Body3",
  render: "p",
  fontFamily: "$text",
  size: "$body3"
});
export const Caption = styled(SizableTextOG, {
  name: "Caption",
  render: "p",
  fontFamily: "$text",
  size: "$caption"
});

// Inline text for emphasis, highlights, or custom styling within block-level typography.
// Inherits font size/family from its parent — use inside Title, Body, Subtitle, etc.
export const TextSpan = styled(TextOG, {
  name: "Span",
  render: "span",
  display: Platform.select({
    web: "inline" as const,
    default: "flex" as const
  }),
  variants: {
    bold: {
      true: {
        fontWeight: "500"
      }
    },
    italic: {
      true: {
        fontStyle: "italic"
      }
    },
    highlight: {
      true: {
        backgroundColor: "$alertPositive" as const,
        // em keeps the padding/radius proportional to the span's inherited font
        // size (it has no size prop to compute against). Web only: RN lays a
        // nested <Text> as an attributed-string run and ignores box props,
        // painting the backgroundColor alone.
        ...Platform.select({
          // why `as unknown as TextProps`: Tamagui doesn't type em on space/
          // radius props, though it passes the value straight through to CSS.
          web: {
            paddingHorizontal: "0.35em",
            paddingVertical: "0.15em",
            borderRadius: "0.3em"
          } as unknown as TextProps,
          default: {}
        })
      }
    }
  } as const
});

// Interface Typography: Trim versions of the Subtitle styles (1.0× line height instead of 1.3×).
// For buttons, badges, chips, and other interactive UI elements.
// Negative margin compensation applied to recreate the trim effect in Figma.

export const Interface1 = styled(Subtitle1, {
  name: "Interface1",
  render: "span",
  size: "$interface1",
  marginTop: Platform.select({
    web: -2,
    default: 0
  }),
  marginBottom: Platform.select({
    web: -2.4,
    default: -3.25
  })
});
export const Interface2 = styled(Subtitle2, {
  name: "Interface2",
  render: "span",
  size: "$interface2",
  marginTop: Platform.select({
    web: -2.3,
    default: 0
  }),
  marginBottom: Platform.select({
    web: -2.1,
    default: -2.75
  })
});
export const Interface3 = styled(Subtitle3, {
  name: "Interface3",
  render: "span",
  size: "$interface3",
  marginTop: Platform.select({
    web: -1.25,
    default: 0
  }),
  marginBottom: Platform.select({
    web: -2.1,
    default: -2.5
  })
});